import React from 'react';
import { Checkbox } from "@/components/ui/checkbox";
import { GripVertical, Lock, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
    LINE_ITEM_BASE,
    LINE_ITEM_SELECTED,
    LINE_ITEM_LOCKED
} from './OrganizeTab.styles';
import { LineItemWithMatchInfo } from '@/api/generated/client/models/LineItemWithMatchInfo';



interface DraggableLineItemProps {
    item: LineItemWithMatchInfo;
    isSelected: boolean;
    onToggle: () => void;
    onEdit?: (item: LineItemWithMatchInfo) => void;
    onDelete?: () => void;
    isLocked: boolean;
    isGhosting?: boolean;
    isOverlay?: boolean;
    showCheckbox?: boolean;
}

export function DraggableLineItem({
    item,
    isSelected,
    onToggle,
    onEdit,
    onDelete,
    isLocked,
    isGhosting,
    isOverlay,
    showCheckbox = true
}: DraggableLineItemProps) {
    // ... existing hook ...
    const { attributes, listeners, setNodeRef, transform, isDragging: isSelfDragging } = useDraggable({
        id: item.lineItemId!,
        disabled: isLocked || isOverlay,
        data: item // Spread all item properties into drag data
    });

    const isDragging = isSelfDragging || isGhosting;

    const style = transform ? {
        transform: CSS.Translate.toString(transform),
    } : undefined;

    return (
        <div
            ref={isOverlay ? undefined : setNodeRef}
            style={isOverlay ? undefined : style}
            className={cn(
                LINE_ITEM_BASE,
                isSelected && LINE_ITEM_SELECTED,
                isDragging && !isOverlay && "opacity-0", // Hide the source item completely
                isLocked && LINE_ITEM_LOCKED,
                isOverlay && "cursor-grabbing shadow-xl scale-105 z-50 ring-2 ring-primary bg-white dark:bg-slate-900 opacity-90" // Enhance overlay
            )}
        >
            {!isLocked && !isOverlay && (
                <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-muted-foreground/30 hover:text-muted-foreground">
                    <GripVertical className="h-3 w-3" />
                </div>
            )}

            {/* Show Grip in overlay but static */}
            {isOverlay && (
                <div className="cursor-grabbing text-muted-foreground/30">
                    <GripVertical className="h-3 w-3" />
                </div>
            )}

            {isLocked ? <Lock className="h-3 w-3 text-muted-foreground/50" /> : null}

            {showCheckbox && (
                <Checkbox checked={isSelected} onCheckedChange={onToggle} disabled={isLocked || isOverlay} className="h-4 w-4" />
            )}



            <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                    <div className="break-words text-xs font-medium text-foreground leading-tight flex-1">{item.description}</div>

                    {/* Action Buttons Group */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        {!isLocked && !isOverlay && onEdit && (
                            <div
                                className="cursor-pointer text-muted-foreground hover:text-primary p-0.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(item);
                                }}
                                title="Edit Line Item"
                            >
                                <Pencil className="h-3 w-3" />
                            </div>
                        )}
                        {!isLocked && !isOverlay && onDelete && (
                            <div
                                className="cursor-pointer text-muted-foreground hover:text-destructive p-0.5 rounded-md hover:bg-red-50 dark:hover:bg-red-950/30"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete();
                                }}
                                title="Delete Line Item"
                            >
                                <Trash2 className="h-3 w-3" />
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 text-[10px] text-muted-foreground leading-tight">
                    {/* Line Item Number */}
                    {item.lineItemNumber && (
                        <span>#{item.lineItemNumber}</span>
                    )}

                    {/* Qty and UOM */}
                    {(item.qty || 0) !== 0 && (
                        <span>
                            {item.qty} {item.uom}
                        </span>
                    )}

                    {/* Unit Price */}
                    {(item.unitPriceCents || 0) !== 0 && (
                        <span>
                            @ {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((item.unitPriceCents || 0) / 100)}
                        </span>
                    )}
                </div>
            </div>

            <div className="text-xs font-medium text-muted-foreground">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((item.rcvCents || 0) / 100)}
            </div>
        </div>
    );
}
